const TYPEFX_STYLE_ID = 'typefx-style';
function getCaretStyle(width = '0.05em') {
    return `
    .typefx-caret{
      position: absolute;
      display: inline-block;
      overflow: visible;
      width: 0px;
    }
    .typefx-caret::after {
      width: 0px;
      border-left: ${width} solid currentColor;
      
      overflow: visible;
      content: "";
      position: relative;
    }
    .typefx-caret.typefx-caret-blink {
      animation: typefx-caret-blink 0.9s steps(1, end) infinite;
    }
    .typefx-selected {
      background-color: #00000044;
    }
    @keyframes typefx-caret-blink { 0%, 49% {opacity: 1;} 50%, 100% { opacity: 0; } }
`;
}
function injectStyle(cssText) {
    if (typeof document === 'undefined')
        return;
    if (document.getElementById(TYPEFX_STYLE_ID))
        return;
    const style = document.createElement('style');
    style.id = TYPEFX_STYLE_ID;
    style.textContent = cssText;
    document.head.appendChild(style);
}
const sleep = (ms) => new Promise(r => setTimeout(r, ms));
function getTextElement(char) {
    const e = document.createElement('span');
    e.textContent = char;
    return e;
}
export default class TypeFX {
    constructor(element, options) {
        this.selectedList = new Set();
        // Internal queue: initially a resolved Promise
        this.q = Promise.resolve();
        this.aborted = false;
        this.el = element;
        this.options = {
            speed: 50,
            speedRange: 50,
            caretWidth: '0.05em',
            ...options
        };
        this.caret = document.createElement('span');
        this.caret.classList.add('typefx-caret', 'typefx-caret-blink');
        // Ensure the caret is inside the container
        if (!this.el.contains(this.caret)) {
            this.el.append(this.caret);
        }
        injectStyle(getCaretStyle(this.options.caretWidth));
    }
    /** Core: attach an async task to the end of the queue and return this for chaining */
    enqueue(task) {
        this.q = this.q.then(() => (this.aborted ? Promise.resolve() : task()));
        return this;
    }
    getSpeedDelay() {
        return this.options.speed + Math.floor(Math.random() * this.options.speedRange) - (this.options.speedRange / 2);
    }
    /** Type: insert string character by character before the caret */
    type(text) {
        return this.enqueue(async () => {
            this.caret.classList.remove('typefx-caret-blink');
            for (const ch of Array.from(text)) {
                if (this.aborted)
                    break;
                let node;
                if (ch === '\n') {
                    node = document.createElement('br');
                }
                else {
                    node = getTextElement(ch);
                }
                this.el.insertBefore(node, this.caret);
                await sleep(this.getSpeedDelay());
            }
            this.caret.classList.add('typefx-caret-blink');
        });
    }
    /** Wait for a period */
    wait(ms) {
        return this.enqueue(async () => { await sleep(ms); });
    }
    /** Delete n characters */
    delete(n = 0) {
        return this.enqueue(async () => {
            this.caret.classList.remove('typefx-caret-blink');
            // If there is a selection, delete the selection first
            if (this.selectedList.size > 0) {
                for (const el of this.selectedList) {
                    this.el.removeChild(el);
                }
                this.selectedList.clear();
                await sleep(this.getSpeedDelay());
            }
            while (n-- > 0) {
                if (this.aborted)
                    break;
                // Find the last node before the caret
                const prev = this.caret.previousSibling;
                if (!prev)
                    break;
                this.el.removeChild(prev);
                await sleep(this.getSpeedDelay());
            }
            this.caret.classList.add('typefx-caret-blink');
        });
    }
    /** Move caret n characters */
    move(n) {
        if (n < 0) {
            return this.enqueue(async () => {
                this.caret.classList.remove('typefx-caret-blink');
                while (n++ < 0) {
                    if (this.aborted)
                        break;
                    // Find the last node before the caret
                    const prev = this.caret.previousSibling;
                    if (!prev)
                        break;
                    this.el.insertBefore(this.caret, prev);
                    await sleep(this.getSpeedDelay());
                }
                this.caret.classList.add('typefx-caret-blink');
            });
        }
        else {
            return this.enqueue(async () => {
                this.caret.classList.remove('typefx-caret-blink');
                while (n-- > 0) {
                    if (this.aborted)
                        break;
                    const next = this.caret.nextSibling;
                    if (!next)
                        break;
                    this.el.insertBefore(this.caret, next.nextSibling);
                    await sleep(this.getSpeedDelay());
                }
                this.caret.classList.add('typefx-caret-blink');
            });
        }
    }
    /** Quick move caret n characters */
    quickMove(n) {
        if (n < 0) {
            return this.enqueue(async () => {
                this.caret.classList.remove('typefx-caret-blink');
                let prev = this.caret;
                let last = prev;
                while (n++ < 0) {
                    last = prev;
                    prev = prev.previousSibling;
                    if (!prev)
                        break;
                }
                this.el.insertBefore(this.caret, last);
                this.caret.classList.add('typefx-caret-blink');
                await sleep(this.getSpeedDelay());
            });
        }
        else {
            return this.enqueue(async () => {
                this.caret.classList.remove('typefx-caret-blink');
                let next = this.caret;
                let last = next;
                while (n-- > 0) {
                    last = next;
                    next = next.nextSibling;
                    if (!next)
                        break;
                }
                this.el.insertBefore(this.caret, last.nextSibling);
                this.caret.classList.add('typefx-caret-blink');
                await sleep(this.getSpeedDelay());
            });
        }
    }
    /** Select n characters */
    select(n) {
        if (n < 0) {
            return this.enqueue(async () => {
                this.caret.classList.remove('typefx-caret-blink');
                while (n++ < 0) {
                    if (this.aborted)
                        break;
                    // Find the last node before the caret
                    const prev = this.caret.previousSibling;
                    if (!prev)
                        break;
                    this.el.insertBefore(this.caret, prev);
                    prev.classList.add("typefx-selected");
                    this.selectedList.add(prev);
                    await sleep(this.getSpeedDelay());
                }
                this.caret.classList.add('typefx-caret-blink');
            });
        }
        else {
            return this.enqueue(async () => {
                this.caret.classList.remove('typefx-caret-blink');
                while (n-- > 0) {
                    if (this.aborted)
                        break;
                    const next = this.caret.nextSibling;
                    if (!next)
                        break;
                    this.el.insertBefore(this.caret, next.nextSibling);
                    next.classList.add("typefx-selected");
                    this.selectedList.add(next);
                    await sleep(this.getSpeedDelay());
                }
                this.caret.classList.add('typefx-caret-blink');
            });
        }
    }
    /** Quick select n characters */
    quickSelect(n) {
        if (n < 0) {
            return this.enqueue(async () => {
                this.caret.classList.remove('typefx-caret-blink');
                let charEl = this.caret.previousSibling;
                if (!charEl)
                    return;
                while (n++ < 0) {
                    if (this.aborted)
                        break;
                    charEl.classList.add("typefx-selected");
                    this.selectedList.add(charEl);
                    // Find the last node before the caret
                    charEl = charEl.previousSibling;
                    if (!charEl)
                        break;
                }
                this.el.insertBefore(this.caret, charEl);
                await sleep(this.getSpeedDelay());
                this.caret.classList.add('typefx-caret-blink');
            });
        }
        else {
            return this.enqueue(async () => {
                this.caret.classList.remove('typefx-caret-blink');
                let charEl = this.caret.nextSibling;
                if (!charEl)
                    return;
                while (n-- > 0) {
                    if (this.aborted)
                        break;
                    charEl.classList.add("typefx-selected");
                    this.selectedList.add(charEl);
                    charEl = charEl.nextSibling;
                    if (!charEl)
                        break;
                }
                this.el.insertBefore(this.caret, charEl);
                await sleep(this.getSpeedDelay());
                this.caret.classList.add('typefx-caret-blink');
            });
        }
    }
    /** Clear content */
    clear() {
        return this.enqueue(async () => {
            while (this.caret.previousSibling) {
                this.el.removeChild(this.caret.previousSibling);
                await sleep(this.getSpeedDelay());
            }
        });
    }
    /** Quick clear content */
    quickClear() {
        return this.enqueue(async () => {
            while (this.caret.previousSibling) {
                this.el.removeChild(this.caret.previousSibling);
            }
        });
    }
    /** Set typing speed */
    speed(ms) {
        return this.enqueue(async () => {
            this.options.speed = ms;
        });
    }
    /** Set typing speed range */
    speedRange(ms) {
        return this.enqueue(async () => {
            this.options.speedRange = ms;
        });
    }
    then(func) {
        return this.enqueue(async () => {
            func();
        });
    }
    /** Cancel all current actions */
    cancel() {
        this.aborted = true;
        this.q.then(() => this.aborted = false);
        return this;
    }
}
